import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Drink, Category } from '../models';
import { API_ENDPOINTS } from '../config/api-endpoints';
import { Page } from '../models/pagination.model';

@Injectable({
  providedIn: 'root'
})
export class DrinksService {

  constructor(private http: HttpClient) { }

  getDrinks(): Observable<Drink[]> {
    return this.http.get<Page<Drink>>(`inventory:${API_ENDPOINTS.DRINKS.LIST}`, { params: { size: '100' } })
      .pipe(map(page => page.content));
  }

  getDrink(id: string | number, barId?: string): Observable<Drink> {
    const params: any = {};
    if (barId) params.barId = barId;
    return this.http.get<Drink>(`inventory:${API_ENDPOINTS.DRINKS.DETAIL(id)}`, { params });
  }

  getCategories(): Observable<Category[]> {
    const staticCategories: Category[] = [
      { id: 1, name: 'Beer' },
      { id: 2, name: 'Wine' },
      { id: 3, name: 'Cocktail' },
      { id: 4, name: 'Whiskey' }
    ];
    return of(staticCategories);
  }

  getLowStockDrinks(): Observable<Drink[]> {
    return this.http.get<Drink[]>(`inventory:${API_ENDPOINTS.DRINKS.LOW_STOCK}`);
  }

  getAvailableMenu(barId: string): Observable<Drink[]> {
    return this.http.get<Drink[]>(`inventory:${API_ENDPOINTS.DRINKS.AVAILABLE}`, { params: { barId } });
  }

  getDrinkByName(name: string): Observable<Drink> {
    return this.http.get<Drink>(`inventory:${API_ENDPOINTS.DRINKS.BY_NAME(name)}`);
  }

  getDrinksByCategory(categoryId: number | string, barId?: string): Observable<Drink[]> {
    const params: any = {};
    if (barId) params.barId = barId;
    return this.http.get<Drink[]>(`inventory:${API_ENDPOINTS.DRINKS.BY_CATEGORY(categoryId)}`, { params });
  }

  getDrinksByCategoryPage(categoryId: number | string, page: number = 0, size: number = 20): Observable<Page<Drink>> {
    return this.http.get<Page<Drink>>(`inventory:${API_ENDPOINTS.DRINKS.BY_CATEGORY_PAGE(categoryId)}`, {
      params: { page: page.toString(), size: size.toString() }
    });
  }

  searchDrinks(filters: {
    name?: string;
    categoryId?: number;
    minPrice?: number;
    maxPrice?: number;
    isAvailable?: boolean;
    page?: number;
    size?: number;
  }): Observable<Page<Drink>> {
    let params = new HttpParams();
    if (filters.name) params = params.set('name', filters.name);
    if (filters.categoryId != null) params = params.set('categoryId', filters.categoryId.toString());
    if (filters.minPrice != null) params = params.set('minPrice', filters.minPrice.toString());
    if (filters.maxPrice != null) params = params.set('maxPrice', filters.maxPrice.toString());
    if (filters.isAvailable != null) params = params.set('isAvailable', filters.isAvailable.toString());
    params = params.set('page', (filters.page ?? 0).toString());
    params = params.set('size', (filters.size ?? 20).toString());
    return this.http.get<Page<Drink>>(`inventory:${API_ENDPOINTS.DRINKS.SEARCH}`, { params });
  }

  isDrinkAvailable(id: number | string): Observable<boolean> {
    return this.http.get<boolean>(`inventory:${API_ENDPOINTS.DRINKS.AVAILABILITY(id)}`);
  }

  getInventoryValue(): Observable<number> {
    return this.http.get<number>(`inventory:${API_ENDPOINTS.DRINKS.INVENTORY_VALUE}`);
  }

  getDrinksByPriceRange(minPrice: number, maxPrice: number): Observable<Drink[]> {
    return this.http.get<Drink[]>(`inventory:${API_ENDPOINTS.DRINKS.PRICE_RANGE}`, {
      params: { minPrice: minPrice.toString(), maxPrice: maxPrice.toString() }
    });
  }

  getTopRatedByCategory(categoryId: number | string, page: number = 0, size: number = 5): Observable<Drink[]> {
    return this.http.get<Drink[]>(`inventory:${API_ENDPOINTS.DRINKS.TOP_RATED(categoryId)}`, {
      params: { page: page.toString(), size: size.toString() }
    });
  }

  createDrink(drink: Partial<Drink>): Observable<Drink> {
    return this.http.post<Drink>(`inventory:${API_ENDPOINTS.DRINKS.LIST}`, drink);
  }

  updateDrink(id: number | string, drink: Partial<Drink>): Observable<Drink> {
    return this.http.put<Drink>(`inventory:${API_ENDPOINTS.DRINKS.DETAIL(id)}`, drink);
  }

  deleteDrink(id: number | string): Observable<void> {
    return this.http.delete<void>(`inventory:${API_ENDPOINTS.DRINKS.DETAIL(id)}`);
  }

  updateStock(id: number | string, stock: number): Observable<Drink> {
    return this.http.patch<Drink>(`inventory:${API_ENDPOINTS.DRINKS.STOCK(id)}`, { stock });
  }

  updateStockPut(id: number | string, quantity: number): Observable<Drink> {
    return this.http.put<Drink>(`inventory:${API_ENDPOINTS.DRINKS.STOCK(id)}`, null, {
      params: { quantity: quantity.toString() }
    });
  }

  reduceStock(id: number | string, quantity: number): Observable<Drink> {
    return this.http.put<Drink>(`inventory:${API_ENDPOINTS.DRINKS.REDUCE_STOCK(id)}`, null, {
      params: { quantity: quantity.toString() }
    });
  }

  bulkUpdateStock(drinkIds: number[], quantity: number): Observable<Drink[]> {
    return this.http.put<Drink[]>(`inventory:${API_ENDPOINTS.DRINKS.BULK_STOCK}`, drinkIds, {
      params: { quantity: quantity.toString() }
    });
  }
}
